<template>
  <Suspense>
    <template #default>
      <router-view />
    </template>
    <template #fallback>
      <div class="loading-container">
        <div class="spinner"></div>
        <p>Loading, please wait...</p>
      </div>
    </template>
  </Suspense>
</template>

<script>

export default {
  name: 'App',
  components: {
  }
}
</script>

<style>

:root {
  /* Primary Colors */
  --primary-color: #3498db;
  --primary-dark: #2980b9;
  --primary-light: #5dade2;
  
  /* Background Colors */
  --bg-primary: #ffffff;
  --bg-secondary: #f8f9fa;
  --bg-tertiary: #e9ecef;
  
  /* Text Colors */
  --text-primary: #2c3e50;
  --text-secondary: #6c757d;
  --text-muted: #95a5a6;
  
  /* Accent Colors */
  --accent-color: #e74c3c;
  --accent-dark: #c0392b;
  --accent-light: #f1948a;
  
  /* Status Colors */
  --success-color: #27ae60;
  --warning-color: #f39c12;
  --error-color: #e74c3c;
  
  /* Spacing */
  --spacing-xs: 0.25rem;
  --spacing-sm: 0.5rem;
  --spacing-md: 1rem;
  --spacing-lg: 1.5rem;
  --spacing-xl: 2rem;
  
  /* Border Radius */
  --border-radius: 0.375rem;
  --border-radius-lg: 0.5rem;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--text-primary);
  background-color: var(--bg-primary);
  
  /* Scrollbar styling for WebKit browsers (Chrome, Safari, Edge) */
  &::-webkit-scrollbar {
    width: 12px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--bg-secondary, #f1f1f1);
    border-radius: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--accent-primary, #888);
    border-radius: 6px;
    border: 2px solid var(--bg-secondary, #f1f1f1);
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: var(--accent-secondary, #555);
  }
}

/* Header Styles */
.route-header {
    background: linear-gradient(135deg, #dc3545, #c82333);
    border-radius: 0 0 20px 20px; /* Top-left: 0, Top-right: 0, Bottom-right: 20px, Bottom-left: 20px */
    padding: 2rem;
    margin-bottom: 2rem;
    box-shadow: 0 20px 60px rgba(220, 53, 69, 0.2);
    color: white;
    position: relative;
    overflow: hidden;
}

.route-header::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 200px;
    height: 200px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    transform: translate(30%, -30%);
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    z-index: 2;
}

.header-main {
    display: flex;
    align-items: center;
    gap: 1.5rem;
}

.header-icon {
    font-size: 3rem;
    opacity: 0.9;
}

.header-text {
    flex: 1;
}

.page-title {
    font-size: 2.2rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
    color: white;
}

.page-subtitle {
    font-size: 1.1rem;
    opacity: 0.9;
    margin: 0;
    color: rgba(255, 255, 255, 0.9);
}

.header-stats {
    display: flex;
    gap: 1rem;
}

.stat-badge {
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    padding: 1.5rem 2rem;
    border-radius: 16px;
    text-align: center;
    min-width: 140px;
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.stat-number {
    display: block;
    font-size: 2.2rem;
    font-weight: 700;
    line-height: 1;
    color: white;
}

.stat-label {
    font-size: 0.9rem;
    opacity: 0.9;
    margin-top: 0.5rem;
    color: rgba(255, 255, 255, 0.9);
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Search Results Container */
.search-results-container {
    margin-bottom: 2rem;
}

.results-card {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 20px;
    padding: 2rem;
    box-shadow: 0 20px 60px rgba(220, 53, 69, 0.1);
    border: 1px solid rgba(220, 53, 69, 0.1);
    backdrop-filter: blur(10px);
}

/* Search Header */
.search-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 2px solid rgba(220, 53, 69, 0.1);
}

.search-title {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.search-icon {
    background: linear-gradient(135deg, #dc3545, #c82333);
    width: 60px;
    height: 60px;
    border-radius: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.5rem;
}

.search-title h3 {
    margin: 0;
    color: #2c3e50;
    font-size: 1.5rem;
    font-weight: 600;
}

.search-title p {
    margin: 0.25rem 0 0 0;
    color: #6c757d;
    font-size: 0.95rem;
}

.search-btn {
    background: linear-gradient(135deg, #dc3545, #c82333);
    color: white;
    border: none;
    padding: 1rem 2rem;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1rem;
    box-shadow: 0 5px 20px rgba(220, 53, 69, 0.3);
}

.search-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(220, 53, 69, 0.4);
}

.search-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
}

.spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}


/* For Firefox */
#app {
  scrollbar-width: thin;
  scrollbar-color: var(--accent-primary, #888) var(--bg-secondary, #f1f1f1);
}

/* For older Edge and IE */
@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
  #app {
    -ms-overflow-style: -ms-autohiding-scrollbar;
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: var(--bg-primary);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--bg-secondary);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Override Bootstrap form-select with modern styling */
.form-select.modern-select {
    /* Keep Bootstrap's core structure but enhance appearance */
    border: 2px solid #e9ecef !important;
    border-radius: 12px !important;
    padding: 0.75rem 1rem !important;
    transition: all 0.3s ease !important;
    background-color: white !important;
    
    /* Custom arrow that overrides Bootstrap's */
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%236c757d' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E") !important;
    background-position: right 1rem center !important;
    background-size: 16px 12px !important;
    padding-right: 3rem !important;
}

/* Focus state - override Bootstrap's focus */
.form-select.modern-select:focus {
    border-color: #dc3545 !important;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
    transform: translateY(-1px);
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23dc3545' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E") !important;
}

/* Hover state - enhance Bootstrap's hover */
.form-select.modern-select:hover {
    border-color: #adb5bd !important;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23495057' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E") !important;
}

/* Disabled state - keep Bootstrap's functionality, enhance appearance */
.form-select.modern-select:disabled {
    background-color: #f8f9fa !important;
    border-color: #e9ecef !important;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23adb5bd' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E") !important;
}

/* Bootstrap validation states - override with modern styling */
.form-select.modern-select.is-valid {
    border-color: #198754 !important;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23198754' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E") !important;
}

.form-select.modern-select.is-valid:focus {
    border-color: #198754 !important;
    box-shadow: 0 0 0 0.2rem rgba(25, 135, 84, 0.25) !important;
}

.form-select.modern-select.is-invalid {
    border-color: #dc3545 !important;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23dc3545' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E") !important;
}

.form-select.modern-select.is-invalid:focus {
    border-color: #dc3545 !important;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

/* Size variants that work with Bootstrap's sizing */
.form-select.modern-select.form-select-sm {
    padding: 0.5rem 0.75rem !important;
    padding-right: 2.5rem !important;
    background-position: right 0.75rem center !important;
}

.form-select.modern-select.form-select-lg {
    padding: 1rem 1.25rem !important;
    padding-right: 3.5rem !important;
    background-position: right 1.25rem center !important;
}

/* Input group compatibility */
.input-group .form-select.modern-select {
    border-radius: 0 12px 12px 0 !important;
    
}

.input-group .form-select.modern-select:focus {
    z-index: 3; /* Maintain Bootstrap's focus z-index */
}

/* For multiple select (if needed) */
.form-select.modern-select[multiple] {
    background-image: none !important;
    padding-right: 1rem !important;
}

.form-select.modern-select[multiple]:focus {
    background-image: none !important;
}

</style>
